import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import axios from 'axios';

const SearchBar = ({ onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSearch = async (searchQuery) => {
    if (searchQuery.trim().length < 2) {
      setResults([]);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post('/api/learning/search', { query: searchQuery });
      setResults(response.data.results || []);
    } catch (error) {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    handleSearch(value);
  };

  const handleResultClick = (result) => {
    navigate(`/learning/${result.category_id}/node/${result.node_id}`);
    onClose();
  };

  return (
    <div className="surface-panel p-5">
      <div className="relative mb-4">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-faint" />
        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          placeholder="Search learning topics…"
          className="input-editorial pl-10 pr-10"
          autoFocus
        />
        {query && (
          <button
            type="button"
            onClick={() => {
              setQuery('');
              setResults([]);
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 icon-btn p-1"
            aria-label="Clear"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        )}
      </div>

      <AnimatePresence mode="wait">
        {loading && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="text-center py-6 text-label text-secondary"
          >
            Searching…
          </motion.p>
        )}

        {!loading && query.length >= 2 && results.length === 0 && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-6 text-label text-secondary"
          >
            No results for &ldquo;{query}&rdquo;
          </motion.p>
        )}

        {!loading && results.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-2 max-h-96 overflow-y-auto hairline-list"
          >
            {results.map((result) => (
              <button
                key={`${result.category_id}-${result.node_id}`}
                type="button"
                onClick={() => handleResultClick(result)}
                className="w-full text-left py-4 px-2 hover:bg-pine-tint/30 transition-colors duration-300 first:pt-2"
              >
                <div className="flex items-start gap-3">
                  <span className="text-xl shrink-0">{result.icon}</span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h4 className="text-body font-medium text-primary">{result.title}</h4>
                      <span className="text-label text-faint capitalize">{result.level}</span>
                    </div>
                    <p className="text-label text-secondary line-clamp-2">{result.description}</p>
                    <p className="text-label text-faint mt-1">
                      {result.category_name} · {result.type} · {result.duration}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SearchBar;
